import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { UUID } from 'crypto'

import { getUploadJobStatus } from '@/api/docsAPI'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useOrgDocs } from '@/utils/hooks/orgs'
import { useTaskContext } from '@/utils/tasks/TaskContext'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import {
  reconcilePages,
  type UploadJobSnapshot,
  type UploadJobState,
} from '../hooks/useBatchScrape'

const TERMINAL_STATUSES = ['completed', 'error', 'cleaned_up']

function isLive(status: UploadJobState | undefined): boolean {
  return status === 'queued' || status === 'running'
}

/**
 * Watches website crawls started by the wizard, from above the step that starts
 * them.
 *
 * Step 8 hands URLs to the crawl queue and moves on, so by the time the crawl
 * finishes the step that submitted it is long unmounted. This lives inside the
 * onboarding provider instead, so a crawl is still watched while the user works
 * through steps 9 and 10 — and is picked up again on a later visit, because the
 * job ids ride along in `wizard_data`, which the shell treats as authoritative
 * on hydration.
 *
 * Registering each live job with TaskContext (which sits above the router) is
 * what puts it in front of the user: the toaster reads from there, so the
 * "your pages are ready" notice still lands if they leave the wizard entirely.
 *
 * Renders nothing.
 */
export default function OnboardingUploadWatcher() {
  const { orgUuid } = useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const queryClient = useQueryClient()
  const { tasks, startTask, trackTaskProgress, clearTask } = useTaskContext()
  const { data: docs } = useOrgDocs(orgUuid as UUID, token)

  const pages = wizardData.pages
  const [jobs, setJobs] = useState<Map<string, UploadJobSnapshot>>(new Map())
  const probedRef = useRef<Set<string>>(new Set())
  const subscriptionsRef = useRef<Map<string, { unsubscribe: () => void }>>(
    new Map(),
  )
  const settledRef = useRef<Set<string>>(new Set())

  const websiteDocs = useMemo(
    () => docs?.filter((d) => d.website_url) ?? [],
    [docs],
  )

  const pageJobIds = useMemo(() => {
    const ids = new Set<string>()
    for (const page of pages) {
      if (page.jobId && page.status !== 'ingested') ids.add(page.jobId)
    }
    return [...ids]
  }, [pages])

  const recordJob = useCallback((jobId: string, snapshot: UploadJobSnapshot) => {
    setJobs((prev) => {
      const next = new Map(prev)
      next.set(jobId, snapshot)
      return next
    })
  }, [])

  /**
   * One status read. Returns null when the server couldn't be asked at all — a
   * blip must not be turned into an outcome, so the page keeps whatever it had
   * and the next mount asks again.
   */
  const readJob = useCallback(
    async (jobId: string): Promise<UploadJobSnapshot | null> => {
      try {
        const status = await getUploadJobStatus({ token, jobId })
        // null is a 404 — jobs expire after 24h. Gone is not failed.
        if (!status) return { status: 'missing' }
        return {
          status: status.status as UploadJobState,
          results: status.results,
        }
      } catch {
        return null
      }
    },
    [token],
  )

  // Ask about each job once. Knowing whether it's still live before subscribing
  // keeps us from opening a stream for a job that expired hours ago.
  useEffect(() => {
    if (!token) return
    for (const jobId of pageJobIds) {
      if (probedRef.current.has(jobId)) continue
      probedRef.current.add(jobId)
      readJob(jobId).then((snapshot) => {
        if (!snapshot) {
          probedRef.current.delete(jobId)
          return
        }
        recordJob(jobId, snapshot)
        if (!isLive(snapshot.status)) {
          // Finished while we weren't looking — the docs it wrote are what the
          // reconcile reads, so make sure they're fresh.
          queryClient.invalidateQueries({ queryKey: ['orgDocs', orgUuid] })
        }
      })
    }
  }, [pageJobIds, token, readJob, recordJob, queryClient, orgUuid])

  // Hand live jobs to TaskContext and subscribe to their progress stream. Both
  // are once-per-job: startTask would otherwise reset a task's status, and the
  // connection drops itself once the last subscriber leaves.
  useEffect(() => {
    if (!token) return
    for (const jobId of pageJobIds) {
      if (!isLive(jobs.get(jobId)?.status)) continue
      if (!tasks[jobId]) {
        startTask('doc_upload', jobId, undefined, orgUuid ?? undefined)
        continue // subscribe on the next pass, once the task exists
      }
      if (subscriptionsRef.current.has(jobId)) continue
      subscriptionsRef.current.set(jobId, trackTaskProgress(jobId, token))
    }
  }, [pageJobIds, jobs, tasks, token, startTask, trackTaskProgress, orgUuid])

  useEffect(() => {
    const subscriptions = subscriptionsRef.current
    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe())
      subscriptions.clear()
    }
  }, [])

  // A job that reached a terminal state — confirm the outcome with the server
  // and refresh the docs it wrote, then let the reconcile below apply both.
  useEffect(() => {
    for (const jobId of pageJobIds) {
      const status = tasks[jobId]?.status
      if (!status || !TERMINAL_STATUSES.includes(status)) continue
      if (settledRef.current.has(jobId)) continue
      settledRef.current.add(jobId)
      readJob(jobId).then((snapshot) => {
        if (!snapshot) return
        if (isLive(snapshot.status)) {
          // TaskContext gave up on it (a task restored from localStorage in a
          // running state for over 20 minutes is written off as interrupted),
          // but the crawl is still going. Drop the stale task and pick the job
          // back up rather than report a failure that hasn't happened.
          settledRef.current.delete(jobId)
          subscriptionsRef.current.get(jobId)?.unsubscribe()
          subscriptionsRef.current.delete(jobId)
          clearTask(jobId)
          recordJob(jobId, snapshot)
          return
        }
        recordJob(jobId, snapshot)
        queryClient.invalidateQueries({ queryKey: ['orgDocs', orgUuid] })
      })
    }
  }, [tasks, pageJobIds, readJob, recordJob, clearTask, queryClient, orgUuid])

  // Reconcile against the DB and the job store. Held off until the docs query
  // has answered: an empty in-flight result would read as "every ingested page
  // was deleted" and drop the lot.
  useEffect(() => {
    if (docs === undefined || pages.length === 0) return
    const reconciled = reconcilePages(pages, websiteDocs, jobs)
    if (JSON.stringify(reconciled) === JSON.stringify(pages)) return
    setWizardData({ pages: reconciled })
  }, [docs, websiteDocs, jobs, pages, setWizardData])

  return null
}
